import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, DialogActions, DialogBody, DialogTitle, DialogTrigger, makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { FixedPriceMutationDto, FixedPriceOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { FormikConfig, useFormikContext } from 'formik'
import {
    bundleIcon,
    DeleteRegular,
    DismissFilled,
    DismissRegular,
    SaveEditFilled,
    SaveEditRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import FixedPriceMutationDialogBodyContent from './FixedPriceMutationDialogBodyContent'
import { useCounter } from 'usehooks-ts'

const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const SaveAndNewIcon = bundleIcon(SaveEditFilled, SaveEditRegular)

const useStyles = makeStyles({
    actions: {
        width: '100%',
    },
})

export default function FixedPriceMutationDialogBody(
    props: HammockFormDialogProps<FixedPriceMutationDto, BaseMutableOverviewDialogProps<FixedPriceOverviewDto>>,
): ReactElement {
    const axios = useAxios()

    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const [submitVariant, setSubmitVariant] = useState<'save' | 'saveAndNew'>('save')
    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)
    const { initialValues, submitForm, isSubmitting, resetForm } = useFormikContext<FixedPriceMutationDto>()

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<FixedPriceOverviewDto>), [props])

    const handleSubmit = useCallback<FormikConfig<FixedPriceMutationDto>['onSubmit']>(
        values =>
            dialogBodyProps.id
                ? axios.put(`/fixed-price/${dialogBodyProps.id}`, values)
                : axios
                      .post(`/fixed-price`, values)
                      .then(() => {
                          if (submitVariant === 'saveAndNew') {
                              resetForm({
                                  values: {
                                      ...initialValues,
                                      vatType: values.vatType,
                                      currency: values.currency,
                                      date: values.date,
                                      userId: values.userId,
                                  },
                              })
                              updateFocusTrigger()
                          } else {
                              resetForm()
                          }
                      })
                      .then(() => submitVariant === 'saveAndNew'),
        [submitVariant],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    const handleDelete = () =>
        axios
            .delete(`/fixed-price/${dialogBodyProps.id}`)
            .then(() => resetForm())
            .then(() => props.onDismiss())
    const handleCancel = () => {
        resetForm()
        props.onDismiss()
    }

    useEffect(() => updateFocusTrigger(), [])

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    return (
        <DialogBody>
            <DialogTitle>{dialogBodyProps.id ? translate('fixedprice.edit.dialog.edit') : translate('fixedprice.edit.dialog.create')}</DialogTitle>
            <FixedPriceMutationDialogBodyContent {...props} focusTrigger={focusTrigger} updateFocusTrigger={updateFocusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    {dialogBodyProps.id && (
                        <Tooltip content={translate('action.delete')} relationship={'label'} withArrow>
                            <Button
                                appearance="subtle"
                                icon={<DeleteRegular />}
                                onClick={() => handleDelete()}
                                disabled={isSubmitting || dialogBodyProps.item?.invoice?.status !== undefined}
                            />
                        </Tooltip>
                    )}
                    {!dialogBodyProps.id && (
                        <Tooltip content={translate('action.saveandnew')} relationship={'label'} withArrow>
                            <Button
                                appearance="subtle"
                                icon={viewportSmall ? <SaveAndNewIcon /> : undefined}
                                onClick={() => {
                                    setSubmitVariant('saveAndNew')
                                    submitForm()
                                }}
                                disabled={isSubmitting || isInvoiceFinal}
                            >
                                {!viewportSmall && translate('action.saveandnew')}
                            </Button>
                        </Tooltip>
                    )}
                    {!dialogBodyProps.id && <div></div>}
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <Tooltip content={translate('action.save')} relationship={'label'} withArrow>
                            <Button
                                appearance="primary"
                                icon={viewportSmall ? <SaveIcon /> : undefined}
                                onClick={() => {
                                    setSubmitVariant('save')
                                    submitForm()
                                }}
                                disabled={isSubmitting || isInvoiceFinal}
                            >
                                {!viewportSmall && translate('action.save')}
                            </Button>
                        </Tooltip>
                        <DialogTrigger disableButtonEnhancement>
                            <Tooltip content={translate('action.cancel')} relationship={'label'} withArrow>
                                <Button
                                    appearance="secondary"
                                    icon={viewportSmall ? <CloseIcon /> : undefined}
                                    onClick={handleCancel}
                                    disabled={isSubmitting}
                                >
                                    {!viewportSmall && translate('action.cancel')}
                                </Button>
                            </Tooltip>
                        </DialogTrigger>
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
